import type { ProjectNodeInputs } from "@oblique/shared/runtime"
import { useCallback, useEffect, useRef, useState } from "react"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import type { BuiltInNodeKind } from "../command-palette/types"
import { ScalarValuePill } from "../pills"
import { VectorValue } from "../vector-value"

export type BuiltInControlKind = Exclude<BuiltInNodeKind, "viewer">

const requiredInputs: Record<BuiltInControlKind, string[]> = {
	point: ["value"],
	slider: ["value", "min", "max"],
	text: ["value"],
	number: ["value"],
	boolean: ["value"],
	button: ["pressed"],
}

export const resolveBuiltInControlKind = (
	metadata: Record<string, unknown>,
	inputs: ProjectNodeInputs,
	outputs: string[],
): BuiltInControlKind | null => {
	const kind = metadata.built_in_kind
	if (
		kind !== "point" &&
		kind !== "slider" &&
		kind !== "text" &&
		kind !== "number" &&
		kind !== "boolean" &&
		kind !== "button"
	) {
		return null
	}
	if (!outputs.includes("result")) return null
	return requiredInputs[kind]?.every((name) => Object.hasOwn(inputs, name))
		? kind
		: null
}

const MomentaryButton = ({
	value,
	onChange,
}: {
	value: boolean
	onChange: (value: boolean) => void
}) => {
	const pressedRef = useRef(value)
	const [pressed, setPressedState] = useState(value)
	const setPressed = useCallback(
		(nextPressed: boolean) => {
			if (pressedRef.current === nextPressed) return
			pressedRef.current = nextPressed
			setPressedState(nextPressed)
			onChange(nextPressed)
		},
		[onChange],
	)
	useEffect(() => {
		pressedRef.current = value
		setPressedState(value)
	}, [value])

	return (
		<button
			type="button"
			aria-pressed={pressed}
			className="nodrag nowheel h-9 w-full rounded-xl border border-border/60 bg-muted/55 px-4 text-sm font-medium shadow-sm transition-colors hover:bg-muted data-[pressed=true]:border-primary data-[pressed=true]:bg-primary data-[pressed=true]:text-primary-foreground"
			data-pressed={pressed}
			onPointerDown={(event) => {
				event.stopPropagation()
				event.currentTarget.setPointerCapture(event.pointerId)
				setPressed(true)
			}}
			onPointerUp={(event) => {
				event.stopPropagation()
				setPressed(false)
			}}
			onPointerCancel={() => setPressed(false)}
			onLostPointerCapture={() => setPressed(false)}
			onBlur={() => setPressed(false)}
			onKeyDown={(event) => {
				if ((event.key === " " || event.key === "Enter") && !event.repeat) {
					event.preventDefault()
					event.stopPropagation()
					setPressed(true)
				}
			}}
			onKeyUp={(event) => {
				if (event.key === " " || event.key === "Enter") {
					event.preventDefault()
					event.stopPropagation()
					setPressed(false)
				}
			}}
		>
			{pressed ? "Pressed" : "Press"}
		</button>
	)
}

export const BuiltInControl = ({
	kind,
	inputs,
	onParameterChange,
}: {
	kind: BuiltInControlKind
	inputs: ProjectNodeInputs
	onParameterChange: (name: string, value: unknown) => void
}) => {
	if (kind === "point") {
		return (
			<div className="nodrag nowheel px-3 py-2">
				<VectorValue
					paramName="value"
					value={inputs.value?.value}
					isConnected={false}
					onChange={(value) => onParameterChange("value", value)}
				/>
			</div>
		)
	}

	if (kind === "slider") {
		const min = Number(inputs.min?.value ?? 0)
		const max = Number(inputs.max?.value ?? 1)
		const value = Number(inputs.value?.value ?? min)
		const low = Math.min(min, max)
		const high = Math.max(min, max)
		return (
			<div className="nodrag nowheel min-w-[340px] space-y-2 px-3 py-2">
				<Slider
					min={low}
					max={high}
					step={Math.max((high - low) / 100, 0.001)}
					value={[Math.min(high, Math.max(low, value))]}
					onValueChange={([nextValue]) => {
						if (nextValue !== undefined) onParameterChange("value", nextValue)
					}}
				/>
				<div className="flex items-center justify-between gap-3">
					<ScalarValuePill
						value={min}
						type="float"
						onChange={(nextValue) => onParameterChange("min", nextValue)}
					/>
					<ScalarValuePill
						value={value}
						type="float"
						emphasized
						onChange={(nextValue) => onParameterChange("value", nextValue)}
					/>
					<ScalarValuePill
						value={max}
						type="float"
						onChange={(nextValue) => onParameterChange("max", nextValue)}
					/>
				</div>
			</div>
		)
	}

	if (kind === "boolean") {
		const checked = inputs.value?.value === true
		return (
			<div className="nodrag nowheel flex items-center justify-between gap-4 px-3 py-2">
				<span className="text-sm text-muted-foreground">
					{checked ? "True" : "False"}
				</span>
				<Switch
					checked={checked}
					onCheckedChange={(value) => onParameterChange("value", value)}
				/>
			</div>
		)
	}

	if (kind === "button") {
		return (
			<div className="px-3 py-2">
				<MomentaryButton
					value={inputs.pressed?.value === true}
					onChange={(value) => onParameterChange("pressed", value)}
				/>
			</div>
		)
	}

	const input = inputs.value
	return (
		<div className="nodrag nowheel px-3 py-2">
			<ScalarValuePill
				value={input?.value}
				type={kind === "number" ? "float" : "string"}
				onChange={(value) => onParameterChange("value", value)}
				className={kind === "text" ? "min-w-[160px]" : undefined}
			/>
		</div>
	)
}
